import { z } from 'zod';
import { ROLES } from './roles.js';
import type { UserDTO } from './user.js';

/**
 * Admin-panel request schemas. Owner-only mutations for managing the team.
 */

/** Set a user's role by their internal id. */
export const setRoleSchema = z
  .object({
    userId: z.string().min(1),
    role: z.enum(ROLES),
  })
  .strict();

export type SetRoleInput = z.infer<typeof setRoleSchema>;

/** Grant/revoke premium for a user (owner-only; payments deferred). */
export const setPremiumSchema = z
  .object({
    userId: z.string().min(1),
    isPremium: z.boolean(),
  })
  .strict();

export type SetPremiumInput = z.infer<typeof setPremiumSchema>;

/** Query for listing users in the admin panel. */
export const listUsersQuerySchema = z
  .object({
    search: z.string().trim().max(80).optional(),
    role: z.enum(ROLES).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .strict();

export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;

/**
 * Per-user attention state surfaced in the admin grid. Purely derived — small
 * overlay badges tell an admin who needs action without opening every card.
 */
export type AttentionKind = 'none' | 'receipt_review' | 'premium_active';

/** A user row for the admin grid, augmented with derived attention state. */
export interface AdminUserListItem extends UserDTO {
  /** Highest-priority attention signal for this user. */
  attention: AttentionKind;
  /** True when this user has a receipt awaiting admin review. */
  pendingReceipt: boolean;
}

/** Admin dashboard counters for filter chips + nav badge. */
export interface AdminAttentionCounts {
  needsAttention: number;
  pendingPayments: number;
  premium: number;
  admins: number;
  total: number;
}

/** Filter presets for the admin user grid. */
export const ADMIN_USER_FILTERS = ['all', 'attention', 'premium', 'payments', 'admins'] as const;
export type AdminUserFilter = (typeof ADMIN_USER_FILTERS)[number];
